import { Row as TanstackRow, Table as TanstackTable } from '@tanstack/react-table';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { CellPropsRenderer, TableData, getTableMeta } from './types';
import { UseTableGridLayoutReturn } from './hooks/useTableGridLayout';
import { getPinnedColumnInfo, getPinnedColumnStyle } from './utils/pinnedColumn';

interface Props<T extends TableData> {
	cell: ReturnType<TanstackRow<T>['getVisibleCells']>[number];
	row: TanstackRow<T>;
	table: TanstackTable<T>;
	gridLayout: UseTableGridLayoutReturn;
	mantineTableBodyCellProps?: CellPropsRenderer<T>;
	hasParentColumns: boolean;
	parentColumnMap: Map<string, string[]>;
	isLastColumn: boolean;
	rowStyles?: React.CSSProperties;
	enableVerticalCellBorders?: boolean;
}

export const MantineTableCell = <T extends TableData>({
	cell,
	row,
	table,
	gridLayout,
	mantineTableBodyCellProps,
	hasParentColumns,
	parentColumnMap,
	isLastColumn,
	rowStyles = {},
	enableVerticalCellBorders = false,
}: Props<T>) => {
	const extraCellProps =
		typeof mantineTableBodyCellProps === 'function'
			? mantineTableBodyCellProps({
					row,
					cell,
					table,
				})
			: mantineTableBodyCellProps ?? {};

	let isLastCellOfParentColumn = false;
	if (hasParentColumns) {
		for (const [, childIds] of parentColumnMap.entries()) {
			const lastChildId = childIds[childIds.length - 1];
			if (cell.column.id === lastChildId) {
				isLastCellOfParentColumn = true;
				break;
			}
		}
	}

	const gridColumn = gridLayout.getGridColumnForColumn(cell.column.id);
	const meta = getTableMeta(table);
	const { isPinned, left, isLast } = getPinnedColumnInfo(
		meta.pinnedColumns,
		meta.lastPinnedColumnId,
		cell.column.id
	);
	const pinnedBackground = isPinned
		? extraCellProps.style?.backgroundColor ?? rowStyles.backgroundColor ?? 'white'
		: undefined;

	const mergedCellProps = {
		...extraCellProps,
		style: {
			gridColumn,
			display: 'flex',
			alignItems: 'center',
			padding: '8px',
			minHeight: '3rem',
			...rowStyles,
			...extraCellProps.style,
			...(enableVerticalCellBorders &&
				!isLastColumn && {
					borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
				}),
			...(isLastCellOfParentColumn &&
				!isLastColumn && {
					borderRight: `1px solid ${defaultTheme.v3.colors.gray[4]}`,
				}),
			...getPinnedColumnStyle(isPinned, Z_INDEX.STICKY_PINNED_COLUMN, {
				backgroundColor: pinnedBackground,
				left,
				withShadow: isLast,
			}),
		},
	};

	return (
		<div key={cell.id} {...mergedCellProps} role="cell">
			{flexRender(cell.column.columnDef.cell, cell.getContext())}
		</div>
	);
};
